import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Avatar,
  Stack,
  Chip,
  Tooltip
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import Board from 'react-trello';
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { useHistory } from 'react-router-dom';
import { format, isSameDay, parseISO } from "date-fns";
import { Can } from "../../components/Can";

const Kanban = () => {
  const themeV4 = useThemeV4();
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [file, setFile] = useState({ lanes: [] });
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));

  const jsonString = user.queues.map(queue => queue.UserQueue.queueId);

  useEffect(() => {
    fetchTags();
  }, [user]);

  const fetchTags = async () => {
    try {
      const response = await api.get("/tag/kanban/");
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
      fetchTickets();
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTickets = async () => {
    try {
      const { data } = await api.get("/ticket/kanban", {
        params: {
          queueIds: JSON.stringify(jsonString),
          startDate: startDate,
          endDate: endDate,
        }
      });
      setTickets(data.tickets);
    } catch (err) {
      console.log(err);
      setTickets([]);
    }
  };

  useEffect(() => {
    const companyId = user.companyId;
    const onAppMessage = (data) => {
      if (data.action === "create" || data.action === "update" || data.action === "delete") {
        fetchTickets();
      }
    };
    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
    };
  }, [socket, startDate, endDate, user.companyId]);

  const handleSearchClick = () => {
    fetchTickets();
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", fontSize: 18 }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", fontSize: 18 }} />;
      case "whatsapp":
        return <WhatsApp style={{ color: "#25d366", fontSize: 18 }} />;
      default:
        return null;
    }
  };

  const popularCards = (jsonString) => {
    const filteredTickets = tickets.filter(ticket => ticket.tags.length === 0);

    const lanes = [
      {
        id: "lane0",
        title: i18n.t("tagsKanban.laneDefault"),
        label: filteredTickets.length.toString(),
        cards: filteredTickets.map(ticket => ({
          id: ticket.id.toString(),
          label: "Ticket nº " + ticket.id.toString(),
          description: (
            <Box sx={{ p: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, pb: 1, borderBottom: "1px solid #e0e0e0" }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                  {ticket.contact.number}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: Number(ticket.unreadMessages) > 0 ? "#16a34a" : "text.secondary",
                    fontWeight: Number(ticket.unreadMessages) > 0 ? 700 : 500,
                    fontSize: "0.75rem"
                  }}
                >
                  {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                    <>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
                  ) : (
                    <>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
                  )}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.8rem",
                  mb: 1,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden"
                }}
              >
                {ticket.lastMessage || " "}
              </Typography>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 1, borderTop: "1px solid #f5f5f5" }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleCardClick(ticket.uuid)}
                  sx={{
                    bgcolor: PRIMARY_MAIN,
                    color: "#fff",
                    fontSize: "0.75rem",
                    textTransform: "none",
                    py: 0.5,
                    px: 1.5,
                    "&:hover": { bgcolor: PRIMARY_DARK }
                  }}
                >
                  Ver Ticket
                </Button>
                {ticket?.user && (
                  <Chip
                    label={ticket.user?.name.toUpperCase()}
                    size="small"
                    sx={{
                      bgcolor: PRIMARY_MAIN,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "0.65rem",
                      height: 20
                    }}
                  />
                )}
              </Stack>
            </Box>
          ),
          title: (
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={ticket.whatsapp?.name}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {IconChannel(ticket.channel)}
                </Box>
              </Tooltip>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                {ticket.contact.name}
              </Typography>
            </Stack>
          ),
          draggable: true,
          href: "/tickets/" + ticket.uuid,
        })),
      },
      ...tags.map(tag => {
        const filteredTickets = tickets.filter(ticket => {
          const tagIds = ticket.tags.map(tag => tag.id);
          return tagIds.includes(tag.id);
        });

        return {
          id: tag.id.toString(),
          title: tag.name,
          label: filteredTickets?.length.toString(),
          cards: filteredTickets.map(ticket => ({
            id: ticket.id.toString(),
            label: "Ticket nº " + ticket.id.toString(),
            description: (
              <Box sx={{ p: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, pb: 1, borderBottom: "1px solid #e0e0e0" }}>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                    {ticket.contact.number}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: Number(ticket.unreadMessages) > 0 ? "#16a34a" : "text.secondary",
                      fontWeight: Number(ticket.unreadMessages) > 0 ? 700 : 500,
                      fontSize: "0.75rem"
                    }}
                  >
                    {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                      <>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
                    ) : (
                      <>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
                    )}
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.8rem",
                    mb: 1,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  }}
                >
                  {ticket.lastMessage || " "}
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 1, borderTop: "1px solid #f5f5f5" }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleCardClick(ticket.uuid)}
                    sx={{
                      bgcolor: PRIMARY_MAIN,
                      color: "#fff",
                      fontSize: "0.75rem",
                      textTransform: "none",
                      py: 0.5,
                      px: 1.5,
                      "&:hover": { bgcolor: PRIMARY_DARK }
                    }}
                  >
                    Ver Ticket
                  </Button>
                  {ticket?.user && (
                    <Chip
                      label={ticket.user?.name.toUpperCase()}
                      size="small"
                      sx={{
                        bgcolor: PRIMARY_MAIN,
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.65rem",
                        height: 20
                      }}
                    />
                  )}
                </Stack>
              </Box>
            ),
            title: (
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title={ticket.whatsapp?.name}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {IconChannel(ticket.channel)}
                  </Box>
                </Tooltip>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                  {ticket.contact.name}
                </Typography>
              </Stack>
            ),
            draggable: true,
            href: "/tickets/" + ticket.uuid,
          })),
          style: { backgroundColor: tag.color, color: "white" }
        };
      }),
    ];

    setFile({ lanes });
  };

  const handleCardClick = (uuid) => {
    history.push('/tickets/' + uuid);
  };

  useEffect(() => {
    popularCards(jsonString);
  }, [tags, tickets]);

  const handleCardMove = async (cardId, sourceLaneId, targetLaneId) => {
    try {
      await api.delete(`/ticket-tags/${targetLaneId}`);
      toast.success('Ticket Tag Removido!');
      await api.put(`/ticket-tags/${targetLaneId}/${sourceLaneId}`);
      toast.success('Ticket Tag Adicionado com Sucesso!');
      await fetchTickets(jsonString);
      popularCards(jsonString);
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddConnectionClick = () => {
    history.push('/tagsKanban');
  };

  return (
    <Box sx={{ minHeight: "100vh", py: { xs: 3, md: 4 }, px: { xs: 2, md: 3 }, bgcolor: "#f8fafc" }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              borderRadius: 3,
              bgcolor: PRIMARY_MAIN,
              "& svg": { width: 28, height: 28, color: "#fff" }
            }}
          >
            <ViewKanbanIcon />
          </Avatar>
          <Box>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: "text.primary",
                mb: 0.5,
                letterSpacing: "-0.5px",
                fontSize: { xs: "1.5rem", md: "1.875rem" }
              }}
            >
              Kanban de Tickets
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
              Gerencie seus tickets de forma visual e organizada
            </Typography>
          </Box>
        </Stack>

        {/* Filtros */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            p: 3,
            mb: 3
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2, fontSize: "1rem" }}>
            Filtros e Configurações
          </Typography>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between">
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Data de início"
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
                sx={{
                  minWidth: 200,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "0.875rem",
                    "& fieldset": { borderColor: "divider" },
                    "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                    "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                  }
                }}
              />
              <TextField
                label="Data de fim"
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
                sx={{
                  minWidth: 200,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "0.875rem",
                    "& fieldset": { borderColor: "divider" },
                    "&:hover fieldset": { borderColor: PRIMARY_MAIN },
                    "&.Mui-focused fieldset": { borderColor: PRIMARY_MAIN }
                  }
                }}
              />
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearchClick}
                sx={{
                  bgcolor: PRIMARY_MAIN,
                  color: "#fff",
                  borderRadius: 2,
                  px: 3,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  textTransform: "none",
                  "&:hover": { bgcolor: PRIMARY_DARK }
                }}
              >
                Buscar
              </Button>
            </Stack>

            <Can role={user.profile} perform="dashboard:view" yes={() => (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddConnectionClick}
                sx={{
                  bgcolor: "#16a34a",
                  color: "#fff",
                  borderRadius: 2,
                  px: 3,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#15803d" }
                }}
              >
                Adicionar Colunas
              </Button>
            )} />
          </Stack>
        </Paper>

        {/* Kanban Board */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            p: 3,
            minHeight: 600
          }}
        >
          <Board
            data={file}
            onCardMoveAcrossLanes={handleCardMove}
            style={{
              backgroundColor: 'transparent',
              height: 'auto',
            }}
          />
        </Paper>
      </Container>
    </Box>
  );
};

export default Kanban;